// +----------------------------------------------------------------------
// | 注册页
// +----------------------------------------------------------------------
import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import useAuth from '../../hooks/useAuth';
import '../../css/auth.less';

export default function Register() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [nickname, setNickname] = useState('');
  const [email, setEmail] = useState('');
  // 协议勾选：不勾不许注册。独立 state，别塞进 validate() 里骗它返回文案 ——
  // 勾选框的提示应该是「先同意协议」，和「密码太短」不是一类事。
  const [agreed, setAgreed] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const { register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // 从哪来的回哪去。公众号导流场景下这是关键：用户点 /free-code 被弹到登录页，
  // 转去注册，注册完必须回到领码页 —— 否则他得自己再找一次入口，多半就流失了。
  // RequireAuth 已经把原路径塞进 state.from，登录页也认这个字段，这里跟它保持一致。
  const from = (location.state && location.state.from) || '/';

  const validate = () => {
    const name = username.trim();
    if (name.length < 3) return '用户名至少 3 个字符';
    if (password.length < 6) return '密码至少 6 位';
    if (password !== confirm) return '两次输入的密码不一致';
    // 邮箱选填；填了就必须是个邮箱（否则后面也收不到重置信）
    const mail = email.trim();
    if (mail !== '' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(mail)) {
      return '邮箱格式不对，检查一下（不填也可以）';
    }
    return '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;

    const msg = validate();
    if (msg) {
      setError(msg);
      return;
    }
    if (!agreed) {
      setError('请先阅读并同意用户协议和隐私政策');
      return;
    }

    setError('');
    setLoading(true);
    try {
      await register({
        username: username.trim(),
        password,
        nickname: nickname.trim() || undefined,
        // 不填就不传这个字段，交给后端存 NULL（空字符串会撞唯一索引）
        email: email.trim() || undefined,
      });
      navigate(from, { replace: true });
    } catch (err) {
      setError(err.message || '注册失败，换一个用户名试试');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth">
      <div className="auth-panel">
        <div className="auth-brand">
          <span className="auth-brand-name">想想</span>
          <span className="auth-brand-sub">从第一次纠结开始，就帮得上忙。</span>
        </div>

        <form className="auth-card" onSubmit={handleSubmit}>
          <h2 className="title-lg">注册</h2>
          <p className="muted" style={{ margin: '6px 0 20px' }}>
            不用填一堆资料，两步就好。
          </p>

          {error && <div className="auth-error">{error}</div>}

          <div className="field">
            <label htmlFor="username">用户名</label>
            <input
              id="username"
              className="input"
              type="text"
              autoComplete="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="3~20 位，字母数字都行"
            />
          </div>

          <div className="field">
            <label htmlFor="nickname">昵称（可不填）</label>
            <input
              id="nickname"
              className="input"
              type="text"
              value={nickname}
              onChange={(e) => setNickname(e.target.value)}
              placeholder="想让我怎么称呼你"
            />
          </div>

          <div className="field">
            <label htmlFor="password">密码</label>
            <input
              id="password"
              className="input"
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="至少 6 位"
            />
          </div>

          <div className="field">
            <label htmlFor="confirm">确认密码</label>
            <input
              id="confirm"
              className="input"
              type="password"
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder="再输一次"
            />
          </div>

          <div className="field">
            <label htmlFor="email">邮箱（建议填）</label>
            <input
              id="email"
              className="input"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="选填"
            />
            <div className="auth-hint">
              找回密码的验证信发到这个邮箱，建议填写；不填的话，忘记密码只能联系客服。只用于找回密码，不发广告。
            </div>
          </div>

          <label className="auth-agree">
            <input
              type="checkbox"
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
            />
            <span>
              我已阅读并同意
              <a href="/agreement.html" target="_blank" rel="noreferrer">《用户服务协议》</a>
              和
              <a href="/privacy.html" target="_blank" rel="noreferrer">《隐私政策》</a>
            </span>
          </label>

          <button type="submit" className="btn btn-primary btn-block auth-submit" disabled={loading}>
            {loading ? '注册中…' : '注册并开始'}
          </button>

          <div className="auth-switch">
            已经有账号？<Link to="/login" state={{ from }}>直接登录</Link>
          </div>
          <div className="auth-docs muted">
            <a href="/agreement.html" target="_blank" rel="noreferrer">用户服务协议</a>
            <span>·</span>
            <a href="/privacy.html" target="_blank" rel="noreferrer">隐私政策</a>
          </div>
        </form>

        <Link to="/" className="auth-back">
          ← 先随便看看
        </Link>
      </div>
    </div>
  );
}
